import React, { useEffect, useState } from 'react';
import useAxios from '../utils/useAxios';
import Swal from 'sweetalert2';
import { FiSave, FiPlus } from 'react-icons/fi';
import { FaEdit, FaTrash, FaCalendarAlt, FaUserPlus, FaBuilding, FaEye, } from 'react-icons/fa';
import { useSidebar } from '../context/SidebarContext';
import styles from '../styles/ConfigurationPage';
import OrganisationSelector from '../components/OrganisationSelector';
import { useAuth } from '../context/AuthContext'; 
import { useNavigate } from 'react-router-dom';

const ConfigurationPage = () => {
  const axiosInstance = useAxios();
  const { sidebarVisible } = useSidebar();
  
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrganisation, setSelectedOrganisation] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Formulaire création / édition
  const [formData, setFormData] = useState({
    organisation: '',
    heure_arrivee_max: '',
    heure_sortie_min: '',
    pause_debut: '',
    pause_fin: '',
    heure_autorisee: '',
    activer_penalites: true,
    type_penalite: 'fixe',
    montant_penalite_retard: '500.00',
    penalite_par_minute: '50.00',
    penalite_par_heure: '1000.00',
    montant_prime_ponctualite: '5000.00',
    type_taux_heure_supplementaire: 'fixe',
    taux_heure_supplementaire: '1000.00',
    taux_minute_supplementaire: '20.00',
    taux_heure_variable: '1100.00',
    is_default: false,
    jours: [], 
  });
  const [editingConfig, setEditingConfig] = useState(null);

  const [joursSemaine, setJoursSemaine] = useState([]);
  const [showModalJours, setShowModalJours] = useState(false);
  const [showModalEmployes, setShowModalEmployes] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [joursAssocies, setJoursAssocies] = useState([]);
  const [employesDisponibles, setEmployesDisponibles] = useState([]);
  const [employesSelectionnes, setEmployesSelectionnes] = useState([]);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoConfig, setInfoConfig] = useState(null);
    // Charger les organisations
  useEffect(() => {
    const fetchOrganisations = async () => {
      try {
        const res = await axiosInstance.get('/organisations/');
        setOrganisations(res.data);
        if (res.data.length > 0 && !selectedOrganisation) {
          setSelectedOrganisation(res.data[0].id);
        }
      } catch (error) {
        Swal.fire('Erreur', 'Impossible de charger les organisations', 'error');
      }
    };
    fetchOrganisations();
  }, []);

  // Gestionnaire de changement d'organisation
  const handleOrganisationChange = (orgId, org) => {
    setSelectedOrganisation(orgId);
    
    fetchConfigs(orgId);
    fetchJoursSemaine();
  };

  // Charger les configurations quand l'organisation change
  useEffect(() => {
    if (selectedOrganisation) {
      fetchConfigs(selectedOrganisation);
      fetchJoursSemaine();
    }
  }, [selectedOrganisation]);

  const fetchConfigs = async (orgId = selectedOrganisation) => {
    if (!orgId) return;
    
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/pointage/parametres/?organisation=${orgId}`);
      setConfigs(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch (error) {
      Swal.fire('Erreur', 'Impossible de charger les configurations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchJoursSemaine = async () => {
    try {
      const res = await axiosInstance.get('/pointage/jour-semaines/');
      setJoursSemaine(res.data);
    } catch {
      Swal.fire("Erreur", "Impossible de charger les jours de la semaine", "error");
    }
  };

  const handleChange = e => {
    const { name, type, value, checked } = e.target;
    setFormData(f => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const toggleJourInForm = (jourId) => {
    setFormData(f => ({
      ...f,
      jours: f.jours.includes(jourId)
        ? f.jours.filter(id => id !== jourId)
        : [...f.jours, jourId],
    }));
  };

  const cleanTime = (timeStr) => {
    if (!timeStr) return null;
    return timeStr.length === 5 ? timeStr + ":00" : timeStr;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!selectedOrganisation) {
      Swal.fire('Erreur', 'Veuillez sélectionner une organisation', 'error');
      return;
    }

    if (formData.is_default) {
      const autresConfigsDefaut = configs.filter(c => c.is_default && (!editingConfig || c.id !== editingConfig.id));
      
      const chevauchement = autresConfigsDefaut.some(c => 
        c.jours.some(jourId => formData.jours.includes(jourId))
      );

      if (chevauchement) {
        Swal.fire(
          "Erreur",
          "Une configuration par défaut existe déjà avec des jours en commun. Veuillez modifier les jours ou désactiver le statut par défaut.",
          "error"
        );
        return;
      }
    }

    if (formData.jours.length === 0) {
      Swal.fire("Erreur", "Veuillez sélectionner au moins un jour pour cette configuration.", "error");
      return;
    }

    const dataToSend = {
      ...formData,
      organisation: selectedOrganisation,
      pause_debut: cleanTime(formData.pause_debut),
      pause_fin: cleanTime(formData.pause_fin),
      heure_autorisee: cleanTime(formData.heure_autorisee),
      montant_penalite_retard: parseFloat(formData.montant_penalite_retard),
      penalite_par_minute: parseFloat(formData.penalite_par_minute),
      penalite_par_heure: parseFloat(formData.penalite_par_heure),
      montant_prime_ponctualite: parseFloat(formData.montant_prime_ponctualite),
      taux_heure_supplementaire: parseFloat(formData.taux_heure_supplementaire),
      taux_minute_supplementaire: parseFloat(formData.taux_minute_supplementaire),
      taux_heure_variable: parseFloat(formData.taux_heure_variable),
      is_default: formData.is_default || false,
      jours: formData.jours,
    };

    try {
      if (editingConfig) {
        await axiosInstance.put(`/pointage/parametres/${editingConfig.id}/`, dataToSend);
        Swal.fire('Succès', 'Configuration modifiée avec succès', 'success');
      } else {
        await axiosInstance.post('/pointage/parametres/', dataToSend);
        Swal.fire('Succès', 'Configuration créée avec succès', 'success');
      }
      resetForm();
      fetchConfigs();
    } catch (error) {
      console.error("Erreur API:", error.response?.data);
      Swal.fire(
        'Erreur',
        error.response?.data.detail
          ? error.response.data.detail
          : JSON.stringify(error.response?.data),
        'error'
      );
    }
  };

  const resetForm = () => {
    setFormData({
      organisation: selectedOrganisation,
      heure_arrivee_max: '',
      heure_sortie_min: '',
      pause_debut: '',
      pause_fin: '',
      heure_autorisee: '',
      activer_penalites: true,
      type_penalite: 'fixe',
      montant_penalite_retard: '500.00',
      penalite_par_minute: '50.00',
      penalite_par_heure: '1000.00',
      montant_prime_ponctualite: '5000.00',
      type_taux_heure_supplementaire: 'fixe',
      taux_heure_supplementaire: '1000.00',
      taux_minute_supplementaire: '20.00',
      taux_heure_variable: '1100.00',
      is_default: false,
      jours: [],
    });
    setEditingConfig(null);
  };

  const startEdit = config => {
    setEditingConfig(config);
    setFormData({
      organisation: config.organisation,
      heure_arrivee_max: config.heure_arrivee_max || '',
      heure_sortie_min: config.heure_sortie_min || '',
      pause_debut: config.pause_debut || '',
      pause_fin: config.pause_fin || '',
      heure_autorisee: config.heure_autorisee || '',
      activer_penalites: config.activer_penalites ?? true,
      type_penalite: config.type_penalite || 'fixe',
      montant_penalite_retard: config.montant_penalite_retard?.toString() || '500.00',
      penalite_par_minute: config.penalite_par_minute?.toString() || '50.00',
      penalite_par_heure: config.penalite_par_heure?.toString() || '1000.00',
      montant_prime_ponctualite: config.montant_prime_ponctualite?.toString() || '5000.00',
      type_taux_heure_supplementaire: config.type_taux_heure_supplementaire || 'fixe',
      taux_heure_supplementaire: config.taux_heure_supplementaire?.toString() || '1000.00',
      taux_minute_supplementaire: config.taux_minute_supplementaire?.toString() || '20.00',
      taux_heure_variable: config.taux_heure_variable?.toString() || '1100.00',
      is_default: config.is_default || false,
      jours: config.jours || [],
    });
  };

  const handleDelete = async config => {
    if (config.is_default) {
      Swal.fire("Erreur", "Impossible de supprimer la configuration par défaut.", "error");
      return;
    }
    const result = await Swal.fire({
      title: 'Confirmer suppression',
      text: `Supprimer cette configuration ?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/pointage/parametres/${config.id}/`);
        Swal.fire('Supprimé', 'Configuration supprimée', 'success');
        if (editingConfig?.id === config.id) resetForm();
        fetchConfigs();
      } catch {
        Swal.fire('Erreur', 'Impossible de supprimer', 'error');
      }
    }
  };

  // --- Modal association jours 
  const openModalJours = async (config) => {
    setSelectedConfig(config);
    try {
      const res = await axiosInstance.get(`/pointage/parametres/${config.id}/jours/`);
      setJoursAssocies(res.data.map(j => j.id));
      setShowModalJours(true);
    } catch {
      Swal.fire("Erreur", "Impossible de récupérer les jours associés", "error");
    }
  };

  const toggleJour = (jourId) => {
    setJoursAssocies(prev => prev.includes(jourId) ? prev.filter(id => id !== jourId) : [...prev, jourId]);
  };

  const saveJoursAssocies = async () => {
    try {
      await axiosInstance.put(`/pointage/parametres/${selectedConfig.id}/associer-jours/`, { jours: joursAssocies });
      Swal.fire("Succès", "Jours associés sauvegardés", "success");
      setShowModalJours(false);
      fetchConfigs();
    } catch {
      Swal.fire("Erreur", "Erreur lors de la sauvegarde", "error");
    }
  };

  // --- Modal association employés
  const openModalEmployes = async (config) => {
    if (config.is_default) {
      Swal.fire("Attention", "Impossible d'associer des employés à la configuration par défaut.", "warning");
      return;
    }
    setSelectedConfig(config);
    try {
      //  Ici on filtre par organisation
      const resEmp = await axiosInstance.get(`/employes/?organisation=${config.organisation}`);
      const resAssoc = await axiosInstance.get(`/pointage/parametres/${config.id}/employes/`);

      setEmployesDisponibles(resEmp.data);
      setEmployesSelectionnes(resAssoc.data.map(e => e.id));
      setShowModalEmployes(true);
    } catch {
      Swal.fire("Erreur", "Impossible de récupérer les employés", "error");
    }
  };

  const toggleEmployeSelection = (empId) => {
    setEmployesSelectionnes(prev => prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]);
  };

  const saveEmployesAssocies = async () => {
    try {
      await axiosInstance.put(`/pointage/parametres/${selectedConfig.id}/associer-employes/`, { employes: employesSelectionnes });
      Swal.fire("Succès", "Employés associés sauvegardés", 'success');
      setShowModalEmployes(false);
      fetchConfigs();
    } catch {
      Swal.fire("Erreur", "Erreur lors de la sauvegarde", "error");
    }
  };

  const openInfoModal = (conf) => {
    setInfoConfig(conf);
    setShowInfoModal(true);
  };
  const currentOrganisation = organisations.find(org => org.id === parseInt(selectedOrganisation));

  return (
    <div style={{...styles.container, marginLeft: sidebarVisible ? 250 : 0}}>
      
      {/* Header avec sélecteur d'organisation */}
      <div style={styles.organisationHeader}>
        <OrganisationSelector 
          onOrganisationChange={handleOrganisationChange}
          selectedOrganisation={selectedOrganisation}
        />
          {(user?.is_super_admin || user?.permissions?.includes("DELETE_ORGANISATION") || user?.permissions?.includes("CREATE_ORGANISATION") || user?.permissions?.includes("UPDATE_ORGANISATION")) && (

            <button
              onClick={() => navigate('/organisations')}
              style={{
                padding: '12px 20px',
                backgroundColor: '#1B019B',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '16px',
                margin: '0 20px',
              }}
            >
              Nom des organisations
            </button>
          )}
        {currentOrganisation && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              Gestion des configurations pour cette organisation
            </span>
          </div>
        )}
      </div>
       

      
      {currentOrganisation ? (
        <>
          
          {/* Formulaire création / édition */}
          {(user?.is_super_admin || user?.permissions?.includes("CREATE_CONFIGURATION") || user?.permissions?.includes("UPDATE_CONFIGURATION")) && (

          <form onSubmit={handleSubmit} style={styles.formContainer}>
            <h4 style={styles.formTitle}>
              {editingConfig ? (
                <>
                  <FaEdit /> Modifier une configuration
                </>
              ) : (
                <>
                  <FiPlus /> Nouvelle configuration
                </>
              )}
              <span style={{ fontSize: '14px', color: '#666', marginLeft: 'auto' }}>
                Organisation: {currentOrganisation.nom}
              </span>
            </h4>

            {/* Horaires */}
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label>Heure maximale d'arrivée</label>
                <input type="time" name="heure_arrivee_max" value={formData.heure_arrivee_max} onChange={handleChange} className="form-control" style={styles.formControl} />
              </div>
              <div style={styles.formGroup}>
                <label>Heure minimale de sortie</label>
                <input type="time" name="heure_sortie_min" value={formData.heure_sortie_min} onChange={handleChange} className="form-control" style={styles.formControl} />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label>Début de la pause</label>
                <input type="time" name="pause_debut" value={formData.pause_debut} onChange={handleChange} className="form-control" style={styles.formControl} />
              </div>
              <div style={styles.formGroup}>
                <label>Fin de la pause</label>
                <input type="time" name="pause_fin" value={formData.pause_fin} onChange={handleChange} className="form-control" style={styles.formControl} />
              </div>
              <div style={styles.formGroup}>
                <label>Heure de pointage (après-midi)</label>
                <input type="time" name="heure_autorisee" value={formData.heure_autorisee} onChange={handleChange} className="form-control" style={styles.formControl} />
              </div>
            </div>

            {/* Pénalités */}
            <div style={{ marginTop: 20 }}>
              <label><input type="checkbox" name="activer_penalites" checked={formData.activer_penalites} onChange={handleChange} /> Activer pénalités</label>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label>Type pénalité</label>
                <select name="type_penalite" value={formData.type_penalite} onChange={handleChange} className="form-select" style={styles.formControl}>
                  <option value="fixe">Fixe</option>
                  <option value="variable_minute">Variable par minute</option>
                  <option value="variable_heure">Variable par heure</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label>Montant fixe</label>
                <input type="number" step="0.01" name="montant_penalite_retard" value={formData.montant_penalite_retard} onChange={handleChange} className="form-control" style={styles.formControl} />
              </div>
              <div style={styles.formGroup}>
                <label>Montant par minute</label>
                <input type="number" step="0.01" name="penalite_par_minute" value={formData.penalite_par_minute} onChange={handleChange} className="form-control" style={styles.formControl} />
              </div>
              <div style={styles.formGroup}>
                <label>Montant par heure</label>
                <input type="number" step="0.01" name="penalite_par_heure" value={formData.penalite_par_heure} onChange={handleChange} className="form-control" style={styles.formControl} />
              </div>
            </div>

            {/* Prime ponctualité */}
            <div style={{ marginTop: 15 }}>
              <label>Montant prime ponctualité</label>
              <input type="number" step="0.01" name="montant_prime_ponctualite" value={formData.montant_prime_ponctualite} onChange={handleChange} className="form-control" style={styles.formControl} />
            </div>

            {/* Taux heures supp */}
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label>Type heure supplémentaire</label>
                <select name="type_taux_heure_supplementaire" value={formData.type_taux_heure_supplementaire} onChange={handleChange} className="form-select" style={styles.formControl}>
                  <option value="fixe">Fixe</option>
                  <option value="variable_minute">Variable par minute</option>
                  <option value="variable_heure">Variable par heure</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label>Montant fixe</label>
                <input type="number" step="0.01" name="taux_heure_supplementaire" value={formData.taux_heure_supplementaire} onChange={handleChange} className="form-control" style={styles.formControl} />
              </div>
              <div style={styles.formGroup}>
                <label>Montant par minute </label>
                <input type="number" step="0.01" name="taux_minute_supplementaire" value={formData.taux_minute_supplementaire} onChange={handleChange} className="form-control" style={styles.formControl} />
              </div>
              <div style={styles.formGroup}>
                <label>Taux par heure</label>
                <input type="number" step="0.01" name="taux_heure_variable" value={formData.taux_heure_variable} onChange={handleChange} className="form-control" style={styles.formControl} />
              </div>
            </div>

            {/* Par défaut */}
            <div style={{ marginTop: 20, fontFamily: "Arial, sans-serif" }}>
              <label style={styles.defaultConfigLabel}>
                <input 
                  type="checkbox" 
                  name="is_default" 
                  checked={formData.is_default} 
                  onChange={handleChange} 
                  style={{ 
                    marginRight: 10, 
                    width: 18, 
                    height: 18, 
                    accentColor: "#4CAF50",
                    cursor: "pointer",
                  }}
                />
                Configurer par défaut
              </label>

              <div 
                style={styles.defaultConfigHelp}
                onMouseEnter={e => e.currentTarget.style.background = "#e8f0e8"}
                onMouseLeave={e => e.currentTarget.style.background = "#f3f7f9"}
              >
                En cochant cette case, cette configuration sera appliquée par défaut à tous 
                les employés <strong>qui n'ont pas déjà de paramètres spécifiques</strong>.  
                Les employés ayant une configuration personnalisée ne seront pas affectés.
              </div>
            </div>

            {/* Sélection jours associés */}
            <div className="mt-4">
              <label>
                <strong style={{ display: "block", fontSize: "15px", marginBottom: "6px" }}>
                  Sélectionnez les jours auxquels cette configuration doit s'appliquer.
                  <span style={{ display: "block", fontStyle: "italic", fontWeight: "400", color: "#555", marginTop: "2px" }}>
                    Les jours non sélectionnés ne seront pas affectés par cette règle.
                  </span>
                </strong>
              </label>
              <div className="d-flex flex-wrap gap-3 mt-1">
                {joursSemaine.map(j => (
                  <div key={j.id} className="form-check">
                    <input
                      type="checkbox"
                      id={`jour-${j.id}`}
                      className="form-check-input"
                      checked={formData.jours.includes(j.id)}
                      onChange={() => toggleJourInForm(j.id)}
                    />
                    <label htmlFor={`jour-${j.id}`} className="form-check-label">{j.nom}</label>
                  </div>
                ))}
              </div>
            </div>

            <button type="submit" className="btn btn-primary mt-4" style={styles.submitButton}>
              <FiSave size={22} style={{ marginRight: 8 }} />
              {editingConfig ? 'Modifier configuration' : 'Ajouter configuration'}
            </button>
            {editingConfig && (
              <button type="button" className="btn btn-secondary mt-4 ms-3" onClick={resetForm}>
                Annuler
              </button>
            )}
          </form>
          )}
          {/* Liste des configs */}
          <div style={styles.configListContainer}>
            <h3 style={styles.configListTitle}>
              <FaBuilding />
              Configurations pour {currentOrganisation.nom}
            </h3>
            
            {loading ? (
              <p style={{ textAlign: 'center', padding: '20px' }}>Chargement des configurations...</p>
            ) : configs.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                Aucune configuration trouvée pour cette organisation.
              </p>
            ) : (
              <table className="table table-bordered table-striped" style={{ fontSize: 15 }}>
                <thead style={styles.tableHeader}>
                  <tr>
                    <th>Heure maximale d'arrivée</th>
                    <th>Heure minimale de sortie</th>
                    <th>Jours associés</th>
                    <th>Par défaut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {configs.map(conf => (
                    <tr key={conf.id}>
                      <td>{conf.heure_arrivee_max}</td>
                      <td>{conf.heure_sortie_min}</td>
                      <td>
                        {conf.jours && conf.jours.length > 0
                          ? joursSemaine.filter(j => conf.jours.includes(j.id)).map(j => j.nom).join(', ')
                          : '—'}
                      </td>
                      <td>{conf.is_default ? 'Oui' : 'Non'}</td>
                      <td>

                        {(user?.is_super_admin || user?.permissions?.includes("VIEW_CONFIGURATION")) && (
                          <button 
                            className="btn btn-sm btn-secondary me-2" 
                            title="Voir détails" 
                            onClick={() => openInfoModal(conf)}
                          >
                            <FaEye />
                          </button>
                        )}

                        {(user?.is_super_admin || user?.permissions?.includes("UPDATE_CONFIGURATION")) && (
                        <button className="btn btn-sm btn-info me-2" title="Modifier" onClick={() => startEdit(conf)}>
                          <FaEdit />
                        </button>
                        )}
                        {(user?.is_super_admin || user?.permissions?.includes("DELETE_CONFIGURATION")) && (
                        <button className="btn btn-sm btn-danger me-2" title="Supprimer" onClick={() => handleDelete(conf)}>
                          <FaTrash />
                        </button>
                        )}
                        {(user?.is_super_admin || user?.permissions?.includes("UPDATE_CONFIGURATION")) && (
                        <button className="btn btn-sm btn-warning me-2" title="Associer jours" onClick={() => openModalJours(conf)}>
                          <FaCalendarAlt />
                        </button>
                        )}
                        {(user?.is_super_admin || user?.permissions?.includes("UPDATE_CONFIGURATION")) && (
                        <button className="btn btn-sm btn-primary" title="Associer employés" onClick={() => openModalEmployes(conf)}>
                          <FaUserPlus />
                        </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '60px', backgroundColor: '#fff', borderRadius: 12, boxShadow: '0 0 8px rgba(0,0,0,0.1)' }}>
          <FaBuilding size={48} style={{ color: '#ccc', marginBottom: '20px' }} />
          <h4 style={{ color: '#666', marginBottom: '10px' }}>Aucune organisation sélectionnée</h4>
          <p style={{ color: '#999' }}>Veuillez sélectionner une organisation pour gérer ses configurations</p>
        </div>
      )}

      {/* Modal association jours */}
      {showModalJours && (
        <div style={styles.modalOverlay}>
          <div className="modal-dialog" role="document">
            <div className="modal-content p-4" style={styles.modalContent}>
              <h5 style={{ color: '#1B019B' }}>Associer les jours à la config</h5>
              <div className="d-flex flex-wrap gap-3 mt-3">
                {joursSemaine.map(j => (
                  <div key={j.id} className="form-check">
                    <input
                      type="checkbox"
                      id={`modal-jour-${j.id}`}
                      className="form-check-input"
                      checked={joursAssocies.includes(j.id)}
                      onChange={() => toggleJour(j.id)}
                    />
                    <label htmlFor={`modal-jour-${j.id}`} className="form-check-label">{j.nom}</label>
                  </div>
                ))}
              </div>
              <div className="mt-4 d-flex justify-content-end gap-3">
                <button className="btn btn-secondary" onClick={() => setShowModalJours(false)}>Annuler</button>
                <button className="btn btn-primary" onClick={saveJoursAssocies} style={{ backgroundColor: '#1B019B' }}>Enregistrer</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal association employés */}
      {showModalEmployes && (
        <div style={styles.modalOverlay}>
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content p-4" style={styles.modalContent}>
              <h5 style={{ color: '#1B019B' }}>Associer employés à la configuration</h5>
              <div style={{ maxHeight: 400, overflowY: 'auto' }} className="mt-3">
                {employesDisponibles.length === 0
                  ? <p>Aucun employé disponible.</p>
                  : employesDisponibles.map(emp => (
                      <div key={emp.id} className="form-check">
                        <input
                          type="checkbox"
                          id={`emp-${emp.id}`}
                          className="form-check-input"
                          checked={employesSelectionnes.includes(emp.id)}
                          onChange={() => toggleEmployeSelection(emp.id)}
                        />
                        <label htmlFor={`emp-${emp.id}`} className="form-check-label">{emp.last_name} {emp.first_name}</label>
                      </div>
                    ))
                }
              </div>
              <div className="mt-4 d-flex justify-content-end gap-3">
                <button className="btn btn-secondary" onClick={() => setShowModalEmployes(false)}>Annuler</button>
                <button className="btn btn-primary" onClick={saveEmployesAssocies} style={{ backgroundColor: '#1B019B', color: '#f6f5faff' }}>Enregistrer</button>
              </div>
            </div>
          </div>
        </div>
      )}



      {showInfoModal && infoConfig && (
        <div
          style={{
            ...styles.modalOverlay,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            paddingLeft: sidebarVisible ? 250 : 0, 
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 10,
              maxWidth: "1000px",
              width: "90%",
              padding: "15px",
              fontSize: "12px", 
              boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
              animation: "fadeIn 0.3s ease",
            }}
          >
            <h4
              style={{
                color: "#1B019B",
                marginBottom: 12,
                textAlign: "center",
                fontSize: "15px",
              }}
            >
              Détails de la configuration
            </h4>

            {/* === SECTION HEURES === */}
            <div style={{ marginBottom: 12 }}>
              <h5 style={{ color: "#1B019B", marginBottom: 6, fontSize: "13px" }}>Horaires</h5>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "6px 10px",
                }}
              >
                <div><strong>Heure max d'arrivée :</strong> {infoConfig.heure_arrivee_max ?? "—"}</div>
                <div><strong>Heure min de sortie :</strong> {infoConfig.heure_sortie_min ?? "—"}</div>
                <div><strong>Début pause :</strong> {infoConfig.pause_debut ?? "—"}</div>
                <div><strong>Fin pause :</strong> {infoConfig.pause_fin ?? "—"}</div>
                <div><strong>Heure pointage (après-midi):</strong> {infoConfig.heure_autorisee ?? "—"}</div>
                <div><strong>Est ce une config par défaut :</strong> {infoConfig.is_default ? "Oui" : "Non"}</div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <strong>Jours concerné par la config  :</strong>{" "}
                  {infoConfig.jours?.map(
                    (jId) => joursSemaine.find((j) => j.id === jId)?.nom
                  ).join(", ") || "—"}
                </div>
              </div>
            </div>

            {/* === SECTION PÉNALITÉS === */}
            <div style={{ marginBottom: 12, paddingTop: 8, borderTop: "1px solid #ddd" }}>
              <h5 style={{ color: "#1B019B", marginBottom: 6, fontSize: "13px" }}>Pénalités</h5>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "6px 10px",
                }}
              >
                <div><strong>Activer :</strong> {infoConfig.activer_penalites ? "Oui" : "Non"}</div>
                <div><strong>Type :</strong> {infoConfig.type_penalite ?? "—"}</div>
                <div><strong>Fixe :</strong> {infoConfig.montant_penalite_retard ?? "—"}</div>
                <div><strong>/min :</strong> {infoConfig.penalite_par_minute ?? "—"}</div>
                <div><strong>/h :</strong> {infoConfig.penalite_par_heure ?? "—"}</div>
              </div>
            </div>

            {/* === SECTION PRIMES === */}
            <div style={{ marginBottom: 12, paddingTop: 8, borderTop: "1px solid #ddd" }}>
              <h5 style={{ color: "#1B019B", marginBottom: 6, fontSize: "13px" }}>Primes</h5>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "6px 10px",
                }}
              >
                <div><strong>Ponctualité :</strong> {infoConfig.montant_prime_ponctualite ?? "—"}</div>
              </div>
            </div>

            {/* === SECTION HEURES SUP === */}
            <div style={{ marginBottom: 12, paddingTop: 8, borderTop: "1px solid #ddd" }}>
              <h5 style={{ color: "#1B019B", marginBottom: 6, fontSize: "13px" }}>
                Heures supplémentaires
              </h5>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "6px 10px",
                }}
              >
                <div><strong>Type :</strong> {infoConfig.type_taux_heure_supplementaire ?? "—"}</div>
                <div><strong>Fixe :</strong> {infoConfig.taux_heure_supplementaire ?? "—"}</div>
                <div><strong>/min :</strong> {infoConfig.taux_minute_supplementaire ?? "—"}</div>
                <div><strong>/h :</strong> {infoConfig.taux_heure_supplementaire ?? "—"}</div>
              </div>
            </div>

          
            <div style={{ textAlign: "right", marginTop: 8 }}>
              <button
                style={{
                  padding: "5px 12px",
                  backgroundColor: "#1B019B",
                  color: "#fff",
                  border: "none",
                  borderRadius: 5,
                  fontSize: "12px",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
                onClick={() => setShowInfoModal(false)}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}






    </div>
  );
};

export default ConfigurationPage;